import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import { DebouncedFunc, debounce } from 'lodash';
import {
  Button,
  Checkbox,
  List,
  ModalView,
  Pagination,
  Stack,
  StyledContainer,
  Text,
  TextField,
  Tooltip,
  withTranslation,
} from '@piwikpro/ui-components';
import {
  ConfigService,
  errorBoundary,
  Pagination as PaginationManager,
  withInject,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { AccessControlService, UserGroupListSort } from '@piwikpro/access-control-crate';
import { UnsavedChangesTracker as UnsavedChangesTrackerComponent } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import { ADD_USER_TO_GROUP_LIST_ID, persistListLimit, readListLimit } from './listLimit';
import { AdministrationState } from '../../../../../redux';

interface AssignableGroup {
  id: string
  name: string
}

const mapStateToProps = (state: AdministrationState) => ({
  user: state.users.details.user.details as NonNullable<
  AdministrationState['users']['details']['user']['details']
  >,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  accessControl: AccessControlService
  userPreferences: any
  routerService: RouterService
  config: ConfigService
  UnsavedChangesTracker: typeof UnsavedChangesTrackerComponent
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
  onMembershipsChanged: () => void
}

const AddToGroup: React.FC<Props> = ({
  accessControl,
  userPreferences,
  routerService,
  config,
  UnsavedChangesTracker,
  user,
  onMembershipsChanged,
  t,
}) => {
  const [groups, setGroups] = useState<AssignableGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<UserGroupListSort>('name');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => readListLimit(
    userPreferences,
    ADD_USER_TO_GROUP_LIST_ID,
    config.get<number>('PAGINATION_ITEMS_PER_PAGE'),
  ));
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [addedIds, setAddedIds] = useState<string[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState<DebouncedFunc<any> | null>(null);
  const lastRequestId = useRef(0);

  const isGroupChecked = (groupId: string) => (
    addedIds.includes(groupId)
    || (memberIds.includes(groupId) && !removedIds.includes(groupId))
  );

  const fetchGroups = async (
    next: { page: number, limit: number, search: string, sort: UserGroupListSort },
    loadMembers = false,
  ) => {
    const requestId = ++lastRequestId.current;

    setIsLoading(true);
    setFailed(false);

    try {
      const pageRequest = accessControl.fetchAssignableUserGroups({
        offset: (next.page - 1) * next.limit,
        limit: next.limit,
        search: next.search,
        sort: next.sort,
      });
      const membersRequest = loadMembers
        ? accessControl.fetchUserGroupMemberIds(user.id)
        : null;
      const [pageResult, ids] = await Promise.all([
        pageRequest,
        membersRequest,
      ]);

      if (requestId !== lastRequestId.current) {
        return;
      }

      setGroups(pageResult.groups);
      setTotal(pageResult.total);

      if (ids) {
        setMemberIds(ids);
      }
    } catch {
      if (requestId !== lastRequestId.current) {
        return;
      }

      setFailed(true);
      setGroups([]);
      setTotal(0);
    } finally {
      if (requestId === lastRequestId.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setSearch('');
    setSort('name');
    setPage(1);
    setAddedIds([]);
    setRemovedIds([]);
    fetchGroups({
      page: 1,
      limit,
      search: '',
      sort: 'name',
    }, true);
  }, [user.id]);

  const handleClose = () => {
    routerService.dismissAdministrationOverlay();
  };

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value;

    if (searchDebounce) {
      searchDebounce.cancel();
    }

    const handleSearch = debounce(() => {
      setPage(1);
      fetchGroups({
        page: 1,
        limit,
        search: nextSearch,
        sort,
      });
    }, config.get('LIST_SEARCH_DELAY'));

    setSearchDebounce(() => handleSearch);
    setSearch(nextSearch);
    handleSearch();
  };

  const handleSortChange = () => {
    const nextSort = sort === 'name' ? '-name' : 'name';
    setSort(nextSort);
    setPage(1);
    fetchGroups({
      page: 1,
      limit,
      search,
      sort: nextSort,
    });
  };

  const handleLimitChange = (nextLimit: number) => {
    persistListLimit(userPreferences, ADD_USER_TO_GROUP_LIST_ID, nextLimit);
    setLimit(nextLimit);
    setPage(1);
    fetchGroups({
      page: 1,
      limit: nextLimit,
      search,
      sort,
    });
  };

  const handlePageChange = (offset: number) => {
    const nextPage = Math.floor(offset / limit) + 1;

    setPage(nextPage);
    fetchGroups({
      page: nextPage,
      limit,
      search,
      sort,
    });
  };

  const toggleGroup = (groupId: string) => {
    const isMember = memberIds.includes(groupId);

    if (isGroupChecked(groupId)) {
      setAddedIds(current => current.filter(id => id !== groupId));

      if (isMember) {
        setRemovedIds(current => (
          current.includes(groupId) ? current : [...current, groupId]
        ));
      }

      return;
    }

    setRemovedIds(current => current.filter(id => id !== groupId));

    if (!isMember) {
      setAddedIds(current => (
        current.includes(groupId) ? current : [...current, groupId]
      ));
    }
  };

  const pagination = new PaginationManager(groups, total, limit, page);
  const selectedOnPage = groups.filter(group => isGroupChecked(group.id));
  const allOnPageSelected = groups.length > 0 && selectedOnPage.length === groups.length;

  const handleSelectPage = (checked: boolean) => {
    const pageIds = groups.map(group => group.id);

    if (checked) {
      setRemovedIds(current => current.filter(id => !pageIds.includes(id)));
      setAddedIds(current => [
        ...current,
        ...pageIds.filter(id => !memberIds.includes(id) && !current.includes(id)),
      ]);
      return;
    }

    setAddedIds(current => current.filter(id => !pageIds.includes(id)));
    setRemovedIds(current => [
      ...current,
      ...pageIds.filter(id => memberIds.includes(id) && !current.includes(id)),
    ]);
  };

  const hasChanges = addedIds.length > 0 || removedIds.length > 0;

  const handleSubmit = async () => {
    if (!hasChanges) {
      return;
    }

    setIsSubmitting(true);

    try {
      await accessControl.updateUserGroupMemberships(user.id, addedIds, removedIds);
      onMembershipsChanged();
      handleClose();
    } catch {
      setIsSubmitting(false);
      fetchGroups({
        page,
        limit,
        search,
        sort,
      });
    }
  };

  return (
    <UnsavedChangesTracker
      discardAction={handleClose}
      type="twoOptions"
      isModified={hasChanges && !isSubmitting}
      render={({ UnsavedChangesModal, onClose }) => (
        <ModalView
          title={t('users.groups.add.title', { email: user.attributes.email })}
          onClose={isSubmitting ? () => {} : onClose}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Button
                appearance="default"
                text={t('components:cancel')}
                onClick={onClose}
                disabled={isSubmitting}
              />
              <Button
                appearance="primary"
                text={t('users.groups.add.submit')}
                onClick={handleSubmit}
                disabled={!hasChanges || isSubmitting}
                pending={isSubmitting}
              />
              <UnsavedChangesModal />
            </Stack>
          )}
        >
          <Stack vertical spacing="narrow">
            <Stack.Item>
              <Text as="p" weight={600}>{t('users.groups.add.selectLabel')}</Text>
            </Stack.Item>
            <Stack.Item fill>
              {!failed && (
                <StyledContainer
                  border={['left', 'right', 'top']}
                  padding="narrow"
                  backgroundColor="gray"
                >
                  <TextField
                    name="search"
                    icon="search"
                    placeholder={t('users.groups.searchPlaceholder')}
                    value={search}
                    onChange={handleSearchChange}
                    clearable
                  />
                </StyledContainer>
              )}
              <List
                columns={[
                  {
                    size: 1,
                    align: 'left',
                    name: (
                      <Stack spacing="narrow" verticalAlignment="center">
                        <Stack.Item>
                          <Checkbox
                            id="select-all-groups"
                            name="select-all-groups"
                            checked={selectedOnPage.length > 0}
                            checkedMixed={selectedOnPage.length > 0 && !allOnPageSelected}
                            disabled={groups.length === 0}
                            onChange={() => handleSelectPage(!allOnPageSelected)}
                          />
                        </Stack.Item>
                        <Stack.Item>
                          <List.SortableHeader
                            text={t('users.groups.column.group')}
                            selected
                            order={sort === 'name' ? 'asc' : 'dsc'}
                            onClick={handleSortChange}
                          />
                        </Stack.Item>
                      </Stack>
                    ),
                  },
                ]}
                error={failed}
                isLoading={isLoading}
              >
                {!failed && groups.map(group => (
                  <List.Item
                    key={group.id}
                    withSelect={{
                      name: group.id,
                      value: group.id,
                      checked: isGroupChecked(group.id),
                      onChange: () => toggleGroup(group.id),
                    }}
                  >
                    <Tooltip content={group.name}>
                      <Text as="p" wrapType="noWrapAndCutText">{group.name}</Text>
                    </Tooltip>
                  </List.Item>
                ))}
              </List>
            </Stack.Item>
            {(!failed && total > 0) && (
              <Stack.Item fill previousSpacing="narrow">
                <Pagination
                  onLimitChange={handleLimitChange}
                  limit={pagination.getLimit()}
                  total={total}
                  limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
                  startIndex={pagination.offset}
                  endIndex={pagination.offset + pagination.getLimit()}
                  onPageChange={handlePageChange}
                />
              </Stack.Item>
            )}
          </Stack>
        </ModalView>
      )}
    />
  );
};

export default connector(withInject<InjectedProps>({
  accessControl: 'AccessControlCrate.accessControl',
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
  config: 'config',
  UnsavedChangesTracker: 'FormCrate.components.UnsavedChangesTracker',
})(withTranslation(['administration', 'components', 'platform'])(errorBoundary(AddToGroup))));
